// src/modules/quantum/QuantumFuture.jsx
// Quantum Future — error correction · fault tolerance · timeline · real hardware · roadmap

import React, { useState, useRef, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = '#f87171';
import { _livePalette as P } from '../../context/paletteSync';
const PI = Math.PI;

const Btn = ({onClick,children,color=AC,disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>
);
const Sl = ({label,value,min,max,step=0.01,onChange,unit='',color=AC}) => (
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{typeof value==='number'?value.toFixed(step<0.01?4:step<1?2:0):value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(parseFloat(e.target.value))}
        style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);

// ─── SURFACE CODE SVG ────────────────────────────────────────────────────────
function SurfaceCodeSVG({ distance, errorRate }) {
  const d = Math.min(distance, 5);
  const n = d * d; // data qubits
  const nAncilla = (d*d - 1); // ancilla qubits
  const cellSize = 42;
  const W = d*cellSize*2+20, H = d*cellSize+30;
  const logicalError = Math.pow(errorRate/0.01, Math.ceil(d/2));

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{background:'#060b14',borderRadius:'8px',border:'1px solid #1e293b',display:'block'}}>
      {/* Data qubits */}
      {Array.from({length:d},(_,row)=>Array.from({length:d},(_,col)=>{
        const x=20+col*cellSize*2+cellSize/2, y=20+row*(cellSize);
        const hasError = Math.random() < errorRate * 0.3;
        return (
          <g key={`d${row}-${col}`}>
            <circle cx={x} cy={y} r={14} fill={hasError?'#f8717130':'#22d3ee18'} stroke={hasError?'#f87171':'#22d3ee'} strokeWidth={hasError?2:1.5}/>
            <text x={x} y={y+4} textAnchor="middle" style={{fontSize:'8px',fill:hasError?'#f87171':'#22d3ee',fontFamily:'JetBrains Mono',fontWeight:700}}>D</text>
          </g>
        );
      }))}

      {/* Ancilla qubits (Z stabilizers) */}
      {Array.from({length:d-1},(_,row)=>Array.from({length:d-1},(_,col)=>{
        const x=20+col*cellSize*2+cellSize+cellSize/2, y=20+row*cellSize+cellSize/2;
        const syndrome = Math.random() < errorRate * 0.4;
        return (
          <g key={`a${row}-${col}`}>
            <rect x={x-12} y={y-12} width={24} height={24} rx={3}
              fill={syndrome?'#a78bfa30':'#4ade8018'} stroke={syndrome?'#a78bfa':'#4ade80'} strokeWidth={syndrome?2:1}
              style={{transform:`rotate(45deg)`,transformOrigin:`${x}px ${y}px`}}/>
            <text x={x} y={y+4} textAnchor="middle" style={{fontSize:'7px',fill:syndrome?'#a78bfa':'#4ade80',fontFamily:'JetBrains Mono',fontWeight:700}}>{syndrome?'✗':'A'}</text>
          </g>
        );
      }))}

      <text x={W/2} y={H-5} textAnchor="middle" style={{fontSize:'8px',fill:'#475569',fontFamily:'JetBrains Mono'}}>
        d={d} surface code · {n} data qubits · {nAncilla} ancilla · logical error ≈{logicalError.toExponential(1)}
      </text>
    </svg>
  );
}

// ─── TIMELINE SVG ────────────────────────────────────────────────────────────
function TimelineSVG({ currentYear }) {
  const W=680, H=200;
  const events = [
    {year:1994,label:"Shor's algo",color:'#22d3ee',y:40},
    {year:1996,label:"Grover's algo",color:'#4ade80',y:80},
    {year:2001,label:'7-qubit NMR',color:'#a78bfa',y:40},
    {year:2016,label:'IBM 5q cloud',color:'#22d3ee',y:80},
    {year:2019,label:'Google 53q supremacy',color:'#fbbf24',y:40},
    {year:2021,label:'127q Eagle (IBM)',color:'#fb923c',y:80},
    {year:2023,label:'1000q Condor',color:'#f87171',y:40},
    {year:2025,label:'NISQ utility',color:'#4ade80',y:80},
    {year:2027,label:'Early FTQC?',color:'#a78bfa',y:40,future:true},
    {year:2030,label:'Logical qubits',color:'#fbbf24',y:80,future:true},
    {year:2035,label:'Fault tolerant',color:'#22d3ee',y:40,future:true},
    {year:2040,label:'Quantum advantage',color:'#f87171',y:80,future:true},
  ];
  const minY=1990, maxY=2042;
  const scaleX = y => 30 + ((y-minY)/(maxY-minY))*(W-60);

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{background:'#060b14',borderRadius:'8px',border:'1px solid #1e293b',display:'block'}}>
      {/* Timeline axis */}
      <line x1={20} y1={H/2} x2={W-20} y2={H/2} stroke="#1e293b" strokeWidth={2}/>

      {/* Current year marker */}
      <line x1={scaleX(currentYear)} y1={20} x2={scaleX(currentYear)} y2={H-20} stroke="#fbbf24" strokeWidth={1.5} strokeDasharray="4 2"/>
      <text x={scaleX(currentYear)} y={14} textAnchor="middle" style={{fontSize:'8px',fill:'#fbbf24',fontFamily:'JetBrains Mono',fontWeight:700}}>NOW</text>

      {events.map((e,i)=>{
        const x=scaleX(e.year), isPast=e.year<=currentYear;
        return (
          <g key={i}>
            <line x1={x} y1={H/2} x2={x} y2={e.y+14} stroke={e.color} strokeWidth={1} strokeDasharray={e.future?'3 2':'none'} opacity={isPast?1:0.5}/>
            <circle cx={x} cy={H/2} r={5} fill={e.color} opacity={isPast?1:0.4}/>
            <text x={x} y={e.year%2===0?e.y:e.y+4} textAnchor="middle" style={{fontSize:'7px',fill:e.color,fontFamily:'JetBrains Mono',fontWeight:700,opacity:isPast?1:0.6}}>{e.label}</text>
            <text x={x} y={e.year%2===0?e.y+12:e.y+16} textAnchor="middle" style={{fontSize:'7px',fill:'#475569',fontFamily:'JetBrains Mono'}}>{e.year}</text>
          </g>
        );
      })}

      {/* Year labels on axis */}
      {[1995,2000,2005,2010,2015,2020,2025,2030,2035,2040].map(y=>(
        <text key={y} x={scaleX(y)} y={H-8} textAnchor="middle" style={{fontSize:'7px',fill:'#334155',fontFamily:'JetBrains Mono'}}>{y}</text>
      ))}
    </svg>
  );
}

// ─── ERROR CORRECTION SIMULATION ─────────────────────────────────────────────
const simQEC = (physicalError, d) => {
  const threshold = 0.01; // ~1% surface code threshold
  const data = [];
  for(let code_d = 1; code_d <= 9; code_d += 2) {
    const t = Math.floor(code_d/2);
    const logicalError = physicalError < threshold
      ? Math.pow(physicalError/threshold, t+1) * (code_d**2)
      : physicalError * 1.2; // above threshold: QEC makes it worse
    const overhead = code_d * code_d * 2; // physical qubits per logical
    data.push({
      d: code_d,
      logicalError: +Math.min(1, logicalError).toExponential(2),
      logicalErrorNum: +Math.min(1, logicalError),
      overhead,
      physicalQubits: overhead,
    });
  }
  return data;
};

const HARDWARE_TABLE = [
  {name:'Superconducting',cos:'IBM, Google, Rigetti',t1:'~100µs',t2:'~100µs',gate:'99.5%',scale:'1000+',color:'#22d3ee'},
  {name:'Trapped Ion',    cos:'IonQ, Quantinuum',   t1:'~1min', t2:'~1s',   gate:'99.9%',scale:'50',   color:'#4ade80'},
  {name:'Photonic',       cos:'PsiQuantum, Xanadu', t1:'Inf',   t2:'~ns',   gate:'99%',  scale:'1M+ planned',color:'#a78bfa'},
  {name:'Neutral Atom',   cos:'QuEra, Pasqal',      t1:'~10s',  t2:'~1s',   gate:'99.5%',scale:'1000+',color:'#fbbf24'},
  {name:'Silicon Spin',   cos:'Intel, SiQure',      t1:'~ms',   t2:'~ms',   gate:'99.8%',scale:'12',   color:'#fb923c'},
  {name:'Topological',    cos:'Microsoft',          t1:'N/A',   t2:'N/A',   gate:'TBD',  scale:'0 (research)',color:'#f87171'},
];

const TABS = ['timeline','error-correction','hardware','fault-tolerance','impact'];

export default function QuantumFuture() {
  const [tab, setTab]   = useState('timeline');
  const [logs, setLogs] = useState([{ts:'00:00:00',msg:'Quantum Future — the road from NISQ to fault-tolerant quantum computing and real-world impact.',t:'info'}]);
  const log = (msg,t='info') => setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  const [currentYear, setCurrentYear] = useState(2026);
  const [physicalError, setPhysicalError] = useState(0.005);
  const [codeDistance, setCodeDistance]   = useState(3);
  const qecData = simQEC(physicalError, codeDistance);

  // Physical qubit needs for Shor's algorithm
  const shorQubits = (n) => Math.round(3*n * (codeDistance**2) * 2);

  // Qubit roadmap data
  const roadmapData = [
    {year:2020,ibm:27,google:54,ionq:11,rigetti:32},
    {year:2021,ibm:127,google:72,ionq:32,rigetti:80},
    {year:2022,ibm:433,google:72,ionq:32,rigetti:80},
    {year:2023,ibm:1121,google:70,ionq:36,rigetti:79},
    {year:2024,ibm:1386,google:105,ionq:36,rigetti:79},
    {year:2025,ibm:4158,google:200,ionq:64,rigetti:100},
    {year:2026,ibm:10000,google:500,ionq:128,rigetti:200},
    {year:2027,ibm:100000,google:1000,ionq:256,rigetti:500},
    {year:2029,ibm:1000000,google:5000,ionq:1024,rigetti:2000},
  ];

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?AC:'transparent'}`,background:tab===t?AC+'18':'transparent',color:tab===t?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* ── TIMELINE ── */}
      {tab==='timeline'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="QUANTUM COMPUTING TIMELINE — HISTORY & PROJECTIONS" accent={AC}>
            <TimelineSVG currentYear={currentYear}/>
            <Sl label="Explore year" value={currentYear} min={1993} max={2042} step={1} onChange={setCurrentYear} color={AC}/>
          </Panel>
          <Panel title="QUBIT COUNT ROADMAP — MAJOR PLAYERS" accent="#22d3ee">
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={roadmapData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                <XAxis dataKey="year" tick={{fontSize:9,fill:'var(--t3)'}}/>
                <YAxis tick={{fontSize:9,fill:'var(--t3)'}} scale="log" domain={['auto','auto']}/>
                <Tooltip/><Legend/>
                <Area type="monotone" dataKey="ibm"    stroke="#22d3ee" fill="#22d3ee22" strokeWidth={2} name="IBM"/>
                <Area type="monotone" dataKey="google" stroke="#4ade80" fill="#4ade8022" strokeWidth={2} name="Google"/>
                <Area type="monotone" dataKey="ionq"   stroke="#a78bfa" fill="#a78bfa22" strokeWidth={2} name="IonQ"/>
                <Area type="monotone" dataKey="rigetti" stroke="#fbbf24" fill="#fbbf2422" strokeWidth={2} name="Rigetti"/>
              </AreaChart>
            </ResponsiveContainer>
            <div style={{fontSize:'8px',color:'#64748b',marginTop:'4px',textAlign:'center'}}>Log scale — post-2025 data are public roadmap projections</div>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px'}}>
            {[
              {era:'Now (NISQ)',desc:'50–1000 noisy qubits. Useful for research, some chemistry. No broad advantage.',color:'#fbbf24'},
              {era:'2027–2030',desc:'First logical qubits. Error-corrected demonstration. Small fault-tolerant algorithms.',color:'#fb923c'},
              {era:'2030–2035',desc:'Hundreds of logical qubits. Shor\'s on small RSA keys. Quantum chemistry utility.',color:'#f87171'},
              {era:'2035+',desc:'Millions of physical qubits → thousands of logical. Full cryptographic-scale Shor\'s, drug design.',color:'#a78bfa'},
            ].map(({era,desc,color})=>(
              <div key={era} style={{padding:'10px',background:'var(--deep)',borderRadius:'4px',borderTop:`2px solid ${color}`}}>
                <div style={{fontSize:'10px',color,fontWeight:700,marginBottom:'5px'}}>{era}</div>
                <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6}}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── ERROR CORRECTION ── */}
      {tab==='error-correction'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'240px 1fr',gap:'12px'}}>
            <Panel title="QEC CONFIG" accent="#4ade80">
              <Sl label="Physical error rate" value={physicalError} min={0.0001} max={0.05} step={0.0001} onChange={setPhysicalError} color="#f87171"/>
              <div style={{padding:'8px',background:physicalError<0.01?'#4ade8010':'#f8717110',borderRadius:'3px',marginBottom:'8px',border:`1px solid ${physicalError<0.01?'#4ade8030':'#f8717130'}`,fontSize:'9px',color:physicalError<0.01?'#4ade80':'#f87171',fontFamily:'JetBrains Mono'}}>
                {physicalError<0.01?'✓ Below threshold (0.01) — QEC helps':'✗ Above threshold — QEC worsens errors'}
              </div>
              {qecData.map(row=>(
                <div key={row.d} style={{display:'flex',justifyContent:'space-between',padding:'4px 6px',background:'var(--deep)',borderRadius:'2px',marginBottom:'2px',fontSize:'9px'}}>
                  <span style={{color:'#4ade80',fontFamily:'JetBrains Mono'}}>d={row.d}</span>
                  <span style={{color:'var(--t3)'}}>{row.physicalQubits}q phys</span>
                  <span style={{color:row.logicalErrorNum<physicalError?'#4ade80':'#f87171',fontFamily:'JetBrains Mono'}}>{row.logicalError}</span>
                </div>
              ))}
            </Panel>
            <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
              <Panel title="SURFACE CODE d×d GRID" accent="#4ade80">
                <SurfaceCodeSVG distance={codeDistance} errorRate={physicalError}/>
                <Sl label="Code distance d" value={codeDistance} min={1} max={5} step={2} onChange={setCodeDistance} color="#4ade80"/>
              </Panel>
              <Panel title="LOGICAL ERROR RATE vs CODE DISTANCE" accent={AC}>
                <ResponsiveContainer width="100%" height={150}>
                  <LineChart data={qecData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="d" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'code distance',fill:'var(--t3)',fontSize:8,position:'insideBottom',offset:-2}}/>
                    <YAxis tick={{fontSize:9,fill:'var(--t3)'}} scale="log" domain={['auto','auto']}/>
                    <Tooltip/>
                    <Line type="monotone" dataKey="logicalErrorNum" stroke="#4ade80" strokeWidth={2} dot name="Logical error rate"/>
                  </LineChart>
                </ResponsiveContainer>
              </Panel>
            </div>
          </div>
          <Panel title="QUANTUM ERROR CORRECTION CODES" accent="#a78bfa">
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px'}}>
              {[
                {name:'Surface Code',qubits:'d² phys → 1 logical',threshold:'~1%',desc:'Planar layout, nearest-neighbor gates only. Best match to superconducting hardware. IBM & Google target.',color:'#22d3ee'},
                {name:'Shor Code',qubits:'9 phys → 1 logical',threshold:'<0.1%',desc:'First QEC code (1995). Concatenated repetition codes. Historical importance, not practical threshold.',color:'#4ade80'},
                {name:'Steane [[7,1,3]]',qubits:'7 phys → 1 logical',threshold:'<0.1%',desc:'CSS code. Transversal Clifford gates. Distance-3: corrects any single error. Simple structure.',color:'#a78bfa'},
                {name:'Toric Code',qubits:'L² phys → 2 logical',threshold:'~11%',desc:'Theoretical best threshold. Requires periodic boundary — hard to implement physically.',color:'#fbbf24'},
                {name:'Color Code',qubits:'7 phys → 1 logical',threshold:'~0.1%',desc:'Transversal T gate — advantage over surface code. 2D triangular lattice.',color:'#fb923c'},
                {name:'Concatenated',qubits:'7ᵏ → 1 logical',threshold:'~0.01%',desc:'Recursive application: 7 qubits protect 1, then 7×7=49 protect that, etc. First rigorous FTQC proof.',color:'#f87171'},
              ].map(({name,qubits,threshold,desc,color})=>(
                <div key={name} style={{padding:'10px',background:'var(--deep)',borderRadius:'3px',borderTop:`2px solid ${color}`}}>
                  <div style={{fontSize:'10px',color,fontWeight:700,marginBottom:'4px'}}>{name}</div>
                  <div style={{fontSize:'9px',color,fontFamily:'JetBrains Mono',marginBottom:'4px',opacity:0.8}}>Overhead: {qubits}</div>
                  <div style={{fontSize:'9px',color:'#64748b',marginBottom:'4px',fontFamily:'JetBrains Mono'}}>Threshold: {threshold}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.5}}>{desc}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── HARDWARE ── */}
      {tab==='hardware'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="QUANTUM HARDWARE PLATFORMS — 2025 LANDSCAPE" accent="#22d3ee">
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'9px'}}>
              <thead><tr style={{background:'var(--deep)'}}>
                {['Platform','Companies','T₁','T₂','2Q fidelity','Scale','Status'].map(h=><th key={h} style={{padding:'7px 10px',textAlign:'left',color:'var(--t3)',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
              </tr></thead>
              <tbody>
                {HARDWARE_TABLE.map((h,i)=>(
                  <tr key={h.name} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'var(--deep)':'transparent'}}>
                    <td style={{padding:'6px 10px',color:h.color,fontWeight:700}}>{h.name}</td>
                    <td style={{padding:'6px 10px',color:'var(--t3)',fontFamily:'JetBrains Mono',fontSize:'9px'}}>{h.cos}</td>
                    <td style={{padding:'6px 10px',color:'#22d3ee',fontFamily:'JetBrains Mono'}}>{h.t1}</td>
                    <td style={{padding:'6px 10px',color:'#4ade80',fontFamily:'JetBrains Mono'}}>{h.t2}</td>
                    <td style={{padding:'6px 10px',color:'#fbbf24',fontFamily:'JetBrains Mono'}}>{h.gate}</td>
                    <td style={{padding:'6px 10px',color:h.color,fontFamily:'JetBrains Mono'}}>{h.scale}</td>
                    <td style={{padding:'6px 10px',color:'var(--t3)'}}>{h.name==='Topological'?'Research':'Production'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
            <Panel title="COHERENCE TIME vs GATE TIME — THE CRITICAL RATIO" accent="#fbbf24">
              <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.8,marginBottom:'8px'}}>
                Key metric: T₂/t_gate = max operations before decoherence
              </div>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={[
                  {platform:'Superc.',ratio:Math.floor(100e-6/50e-9)},
                  {platform:'Trapped Ion',ratio:Math.floor(1/1e-3)},
                  {platform:'Neutral Atom',ratio:Math.floor(10/1e-3)},
                  {platform:'Photonic',ratio:1000},
                  {platform:'Silicon Spin',ratio:Math.floor(1e-3/10e-9)},
                ]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="platform" tick={{fontSize:8,fill:'var(--t3)'}}/>
                  <YAxis tick={{fontSize:9,fill:'var(--t3)'}} scale="log" domain={['auto','auto']}/>
                  <Tooltip/>
                  <Bar dataKey="ratio" name="T₂/t_gate ops" radius={[2,2,0,0]}>
                    {[0,1,2,3,4].map(i=><Cell key={i} fill={P[i%P.length]}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Panel>
            <Panel title="PHYSICAL QUBITS NEEDED FOR USEFUL ALGORITHMS" accent="#a78bfa">
              <div style={{display:'flex',flexDirection:'column',gap:'4px'}}>
                {[
                  {alg:"Shor's (2048-bit RSA)",physical:"~4M qubits (d=27)",years:"2035+",color:'#f87171'},
                  {alg:'VQE (50-atom molecule)',physical:"~1M qubits",years:"2030s",color:'#fb923c'},
                  {alg:"Grover's (64-bit search)",physical:"~100K qubits",years:"2030s",color:'#fbbf24'},
                  {alg:'Small quantum chemistry',physical:"~1000 qubits",years:"2027-2030",color:'#4ade80'},
                  {alg:'QAOA (100-node graph)',physical:"~500 qubits (noisy)",years:"Now (NISQ)",color:'#22d3ee'},
                  {alg:'Proof-of-concept VQE',physical:"~20 qubits",years:"Available now",color:'#a78bfa'},
                ].map(({alg,physical,years,color})=>(
                  <div key={alg} style={{display:'flex',gap:'8px',padding:'6px 8px',background:'var(--deep)',borderRadius:'2px',borderLeft:`2px solid ${color}`}}>
                    <span style={{fontSize:'9px',color,fontWeight:700,minWidth:200}}>{alg}</span>
                    <span style={{fontSize:'9px',color:'var(--t3)',minWidth:130,fontFamily:'JetBrains Mono'}}>{physical}</span>
                    <span style={{fontSize:'9px',color:color,fontFamily:'JetBrains Mono'}}>{years}</span>
                  </div>
                ))}
              </div>
            </Panel>
          </div>
        </div>
      )}

      {/* ── FAULT TOLERANCE ── */}
      {tab==='fault-tolerance'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="FAULT TOLERANT QUANTUM COMPUTING — THE PATH" accent="#4ade80">
            <div style={{display:'flex',flexDirection:'column',gap:'4px'}}>
              {[
                {step:'1. Physical qubit',done:true, detail:'Noisy qubit. Error rate ~0.1–1%. Superconducting, ion, etc. Available NOW.',color:'#4ade80'},
                {step:'2. QEC encoding',done:true, detail:'Encode 1 logical qubit in d² physical qubits. Surface code distance ~5. Demonstrated in lab.',color:'#4ade80'},
                {step:'3. Repeated syndrome detection',done:true, detail:'Ancilla qubits detect errors without measuring data. Stabilizer measurement circuits. Demonstrated.',color:'#4ade80'},
                {step:'4. Real-time decoding',done:false,detail:'Classical decoder (MWPM or ML) processes syndrome data faster than qubit error rate. Active research.',color:'#fbbf24'},
                {step:'5. Magic state distillation',done:false,detail:'Non-Clifford T gates require expensive distillation. 15 noisy T gates → 1 clean T gate. Big overhead.',color:'#fbbf24'},
                {step:'6. Logical gate concatenation',done:false,detail:'Apply gates to logical qubits without destroying error correction. Fault-tolerant Clifford available; T gate hard.',color:'#fb923c'},
                {step:'7. Full fault-tolerant algorithm',done:false,detail:"All gates fault-tolerant, below threshold, enough logical qubits for Shor's. Estimated 2035+.",color:'#f87171'},
              ].map(({step,done,detail,color})=>(
                <div key={step} style={{display:'flex',gap:'12px',padding:'8px 12px',background:'var(--deep)',borderRadius:'3px',borderLeft:`3px solid ${color}`}}>
                  <span style={{fontSize:'12px',minWidth:16}}>{done?'✓':'○'}</span>
                  <span style={{fontSize:'9px',color,fontFamily:'JetBrains Mono',fontWeight:700,minWidth:200}}>{step}</span>
                  <span style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.5}}>{detail}</span>
                </div>
              ))}
            </div>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
            <Panel title="THE THRESHOLD THEOREM" accent="#22d3ee">
              <div style={{padding:'12px',background:'var(--deep)',borderRadius:'4px',marginBottom:'10px',borderLeft:'3px solid #22d3ee'}}>
                <div style={{fontFamily:'JetBrains Mono',fontSize:'11px',color:'#22d3ee',marginBottom:'6px'}}>p_phys &lt; p_threshold → FTQC possible</div>
                <div style={{fontSize:'9px',color:'#64748b'}}>Surface code threshold ≈ 1%</div>
              </div>
              <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.8}}>
                If physical error rate p &lt; threshold p_th, then logical error rate can be made arbitrarily small by increasing code distance d:
              </div>
              <div style={{padding:'8px',background:'var(--deep)',borderRadius:'3px',fontFamily:'JetBrains Mono',fontSize:'9px',color:'#22d3ee',marginTop:'6px',lineHeight:2}}>
                p_L ≈ (p/p_th)^⌈d/2⌉<br/>
                IBM best: ~0.15% gate error ✓<br/>
                Quantinuum: ~0.04% ✓✓<br/>
                Target: &lt;0.1% with QEC
              </div>
            </Panel>
            <Panel title="T GATE DISTILLATION OVERHEAD" accent="#fbbf24">
              <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.8,marginBottom:'8px'}}>
                Clifford gates (H, CNOT, S) are "free" in fault-tolerant QC. The T gate requires expensive magic state distillation — the dominant cost of FTQC.
              </div>
              <ResponsiveContainer width="100%" height={130}>
                <BarChart data={[
                  {round:1,qubits:15,fidelity:'99%'},
                  {round:2,qubits:225,fidelity:'99.99%'},
                  {round:3,qubits:3375,fidelity:'99.9999%'},
                ]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="round" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'distillation round',fill:'var(--t3)',fontSize:8,position:'insideBottom',offset:-2}}/>
                  <YAxis tick={{fontSize:9,fill:'var(--t3)'}} scale="log" domain={['auto','auto']}/>
                  <Tooltip/>
                  <Bar dataKey="qubits" fill="#fbbf24" radius={[2,2,0,0]} name="Physical qubits needed"/>
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          </div>
        </div>
      )}

      {/* ── IMPACT ── */}
      {tab==='impact'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
            {[
              {domain:'Cryptography',timeline:'2030s (FTQC)',icon:'🔐',impact:'Shor breaks RSA, ECC. Post-quantum cryptography (CRYSTALS, SPHINCS+) being standardized by NIST now.',urgency:'🚨 Critical',color:'#f87171'},
              {domain:'Drug Discovery',timeline:'2027-2035',icon:'💊',impact:'VQE simulates molecular binding. Could reduce drug development from 15yr/$2B to much less. Protein folding.',urgency:'⚡ High value',color:'#4ade80'},
              {domain:'Climate / Energy',timeline:'2028-2035',icon:'🌍',impact:'Haber-Bosch optimization (2% world energy). Better catalysts for solar cells, batteries, CO₂ capture.',urgency:'⚡ High value',color:'#22d3ee'},
              {domain:'Finance',timeline:'2025-2030',icon:'📈',impact:'Portfolio optimization, derivative pricing, Monte Carlo speedup (Grover). D-Wave already used by banks.',urgency:'Medium',color:'#fbbf24'},
              {domain:'Logistics',timeline:'2025-2028',icon:'🚚',impact:'Vehicle routing, supply chain optimization as QUBO on quantum annealers. Limited but real NISQ utility.',urgency:'Medium',color:'#fb923c'},
              {domain:'AI / ML',timeline:'2030+',icon:'🧠',impact:'Quantum speedup for kernel methods uncertain. Long-term: quantum neural nets, quantum transformers.',urgency:'Uncertain',color:'#a78bfa'},
            ].map(({domain,timeline,icon,impact,urgency,color})=>(
              <Panel key={domain} title={`${icon} ${domain}`} accent={color}>
                <div style={{fontSize:'9px',marginBottom:'4px'}}><span style={{color:'var(--t3)'}}>Timeline: </span><span style={{color,fontFamily:'JetBrains Mono',fontWeight:700}}>{timeline}</span></div>
                <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6,marginBottom:'5px'}}>{impact}</div>
                <div style={{padding:'3px 8px',background:color+'18',borderRadius:'2px',display:'inline-block',fontSize:'8px',color,fontFamily:'JetBrains Mono',fontWeight:700}}>{urgency}</div>
              </Panel>
            ))}
          </div>
          <Panel title="POST-QUANTUM CRYPTOGRAPHY — WHAT TO DO NOW" accent="#f87171">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
              <div>
                <div style={{fontSize:'10px',color:'#f87171',fontWeight:700,marginBottom:'6px'}}>Algorithms broken by Shor's</div>
                {['RSA (all key sizes)','Elliptic Curve Cryptography (ECC)','Diffie-Hellman key exchange','ElGamal','DSA signatures'].map(a=>(
                  <div key={a} style={{padding:'4px 8px',background:'#f8717110',borderRadius:'2px',marginBottom:'2px',borderLeft:'2px solid #f87171',fontSize:'9px',color:'var(--t3)'}}>✗ {a}</div>
                ))}
              </div>
              <div>
                <div style={{fontSize:'10px',color:'#4ade80',fontWeight:700,marginBottom:'6px'}}>NIST PQC Standards (2024)</div>
                {[
                  {name:'CRYSTALS-Kyber',use:'Key encapsulation (KEM)',color:'#22d3ee'},
                  {name:'CRYSTALS-Dilithium',use:'Digital signatures',color:'#4ade80'},
                  {name:'FALCON',use:'Lattice-based signatures',color:'#a78bfa'},
                  {name:'SPHINCS+',use:'Hash-based signatures',color:'#fbbf24'},
                ].map(({name,use,color})=>(
                  <div key={name} style={{padding:'5px 8px',background:'#4ade8010',borderRadius:'2px',marginBottom:'3px',borderLeft:`2px solid ${color}`,display:'flex',justifyContent:'space-between',fontSize:'9px'}}>
                    <span style={{color,fontWeight:700}}>{name}</span>
                    <span style={{color:'var(--t3)'}}>{use}</span>
                  </div>
                ))}
                <div style={{marginTop:'6px',fontSize:'9px',color:'var(--t3)',padding:'6px',background:'var(--deep)',borderRadius:'3px',lineHeight:1.6}}>
                  Google Chrome, Apple iOS, Cloudflare already deploying PQC. Migrate your crypto NOW — "harvest now, decrypt later" attacks already happening.
                </div>
              </div>
            </div>
          </Panel>
          <LogPanel logs={logs} height={70}/>
        </div>
      )}
    </div>
  );
}
